import React, { useEffect, useState, useRef, useMemo } from 'react';
import { CircularProgress, Menu, MenuItem } from '@mui/material';
import { Maximize2, Minimize2, EllipsisVertical, Download, TableProperties, ChartLine, ImageDown, FileDown } from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { SiteChartsConfig } from '../SiteCharts/SiteCharts';
import { ThemeColorName, getThemeAwareChartColors } from '../../theme/chartColors';
import { useIsDarkMode } from '../../theme/hooks';
import { LOCAL_STORAGE } from '../../globals/globals';
import { exportGlobalChartPdf } from '../../pages/ExternalPerformance/exportPagePdf';

dayjs.extend(utc);

interface GlobalChartsProps {
  config: SiteChartsConfig;
  onFullscreenToggle: () => void;
  isFullscreen: boolean;
  /** Time range filter value */
  timeRange: string;
  /** Aggregation interval */
  truncate: string;
  /** Custom start date (YYYY-MM-DD) */
  startDate?: string | null;
  /** Custom end date (YYYY-MM-DD) */
  endDate?: string | null;
  /** Increment to trigger a re-fetch */
  refreshTrigger?: number;
  /** Pre-fetched data from batch endpoint — skips individual fetch when provided */
  prefetchedData?: any[] | null;
  /** Loading state from parent batch fetch */
  prefetchedLoading?: boolean;
  /** Region filter — empty/undefined means all regions */
  regions?: string[];
}

/**
 * GlobalCharts Template — Config-driven chart component for global (non-device) data.
 *
 * Mirrors the SiteCharts template but does NOT depend on selectedDevice or PerformanceContext.
 * Time range, interval, and refresh are passed as props from the parent page.
 *
 * Features:
 * - Config-driven (reuse SiteChartsConfig type)
 * - Built-in: Chart/Table toggle, fullscreen, CSV export, image export
 * - JWT authentication included
 * - Tailwind card styling, Lucide icons, theme-aware ECharts colors
 *
 * @see SiteCharts — for device-scoped charts
 * @see SiteChartsConfig — shared config type
 */
const GlobalCharts: React.FC<GlobalChartsProps> = ({
  config,
  onFullscreenToggle,
  isFullscreen,
  timeRange,
  truncate,
  startDate,
  endDate,
  refreshTrigger,
  prefetchedData,
  prefetchedLoading,
  regions,
}) => {
  const isDarkMode = useIsDarkMode();
  const themeColors = useMemo(() => getThemeAwareChartColors(), [isDarkMode]);

  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const storageKey = `chart-view-${config.title}`;
  const [showTable, setShowTable] = useState<boolean>(() => {
    const stored = sessionStorage.getItem(storageKey);
    return stored === 'true';
  });

  const chartRef = useRef<ReactECharts>(null);
  const menuOpen = Boolean(anchorEl);
  const [pdfLoading] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleExportPdf = () => {
    handleMenuClose();
    const chartDataUrl = chartRef.current?.getEchartsInstance().getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: '#ffffff',
    });
    if (!chartDataUrl) return;
    exportGlobalChartPdf({
      title: config.title,
      csvFilename: config.csvFilename,
      chartDataUrl,
      timeRange,
    });
  };

  const handleToggleView = () => {
    const newValue = !showTable;
    setShowTable(newValue);
    sessionStorage.setItem(storageKey, String(newValue));
    handleMenuClose();
  };

  const handleExportCSV = () => {
    if (chartData.length === 0) {
      alert('No data available to export');
      return;
    }
    const headers = ['Timestamp', ...config.tableColumns.map(col => col.header)];
    const rows = chartData.map(d => {
      const row = [dayjs.utc(d.timestamp).format('YYYY-MM-DD HH:mm:ss')];
      config.tableColumns.forEach(col => {
        const value = d[col.dataKey];
        row.push(col.isPercentage ? value.toFixed(2) : value);
      });
      return row;
    });
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${config.csvFilename}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleMenuClose();
  };

  const handleSaveAsImage = () => {
    const chartInstance = chartRef.current?.getEchartsInstance();
    if (chartInstance) {
      const url = chartInstance.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: themeColors.background,
      });
      const link = document.createElement('a');
      link.href = url;
      link.download = `${config.chartImageFilename}_${dayjs().format('YYYY-MM-DD_HH-mm-ss')}.png`;
      link.click();
    }
    handleMenuClose();
  };

  // When prefetchedData is provided, use it directly instead of fetching
  useEffect(() => {
    if (prefetchedData !== undefined && prefetchedData !== null) {
      setChartData(prefetchedData);
      setLoading(false);
      setError(prefetchedData.length === 0 ? 'No data available for the selected time range' : null);
      return;
    }
    if (prefetchedLoading) {
      setLoading(true);
      return;
    }
  }, [prefetchedData, prefetchedLoading]);

  // Individual fetch — only when no prefetchedData is provided
  useEffect(() => {
    if (prefetchedData !== undefined) return; // batch mode — skip individual fetch

    const fetchChartData = async () => {
      if (timeRange === 'custom' && (!startDate || !endDate)) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const urlParams = new URLSearchParams({ truncate });

        if (timeRange === 'custom' && startDate && endDate) {
          urlParams.set('start_date', startDate);
          urlParams.set('end_date', endDate);
        } else {
          urlParams.set('time_range', timeRange);
        }

        if (regions && regions.length > 0) {
          regions.forEach(r => urlParams.append('region_name', r));
        }

        const apiBaseUrl = '/v1/charts';
        const token = localStorage.getItem(LOCAL_STORAGE.TOKEN);

        const response = await fetch(
          `${apiBaseUrl}${config.apiEndpoint}?${urlParams}`,
          {
            credentials: 'include',
            headers: {
              'Accept': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` }),
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.length === 0) {
          setError('No data available for the selected time range');
        }
        setChartData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [timeRange, truncate, startDate, endDate, config.apiEndpoint, config.title, refreshTrigger, regions]);

  const chartHeight = isFullscreen
    ? Math.min(window.innerHeight - 350, 600)
    : 400;

  const resolveColor = (color: ThemeColorName | string): string => {
    if (color in themeColors) return themeColors[color as ThemeColorName];
    return color;
  };

  const formatK = (value: number): string => {
    if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
    return String(value);
  };

  const getOption = () => {
    if (chartData.length === 0) return {};

    const timestamps = chartData.map(d => dayjs.utc(d.timestamp).format('MMM DD'));

    const seriesData = config.series.map(seriesConfig => {
      const resolvedColor = resolveColor(seriesConfig.color);
      return {
        name: seriesConfig.name,
        type: seriesConfig.type,
        yAxisIndex: seriesConfig.yAxisIndex,
        data: chartData.map(d => {
          const v = d[seriesConfig.dataKey];
          return (v == null) ? { value: null } : v;
        }),
        itemStyle: { color: resolvedColor },
        ...(seriesConfig.type === 'line'
          ? {
              smooth: false,
              lineStyle: { color: resolvedColor, width: 2 },
              symbol: 'circle',
              symbolSize: 6,
            }
          : {}),
        emphasis: { focus: 'series' },
      };
    });

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: themeColors.tooltipBg,
        borderColor: themeColors.cardBorder,
        textStyle: { color: themeColors.foreground },
        axisPointer: { type: 'line', lineStyle: { color: themeColors.axisLabel, type: 'dashed' } },
        formatter: (params: any) => {
          if (!params || params.length === 0) return '';
          let result = `<strong>${params[0].axisValue}</strong><br/>`;
          params.forEach((param: any) => {
            if (param.value == null) return;
            const sc = config.series.find(s => s.name === param.seriesName);
            const value = sc?.isPercentage
              ? `${Number(param.value).toFixed(2)}%`
              : formatK(Number(param.value));
            result += `${param.marker} ${param.seriesName}: ${value}<br/>`;
          });
          return result;
        },
      },
      toolbox: {
        feature: { dataZoom: { yAxisIndex: 'none' }, restore: {} },
        right: 20,
        top: 5,
      },
      legend: {
        data: config.series.map(s => s.name),
        top: 35,
        itemGap: 20,
        textStyle: { fontSize: 12, color: themeColors.axisLabel },
      },
      grid: { left: 80, right: 80, bottom: 50, top: 70, containLabel: false },
      xAxis: {
        type: 'category',
        data: timestamps,
        axisLabel: { fontSize: 11, color: themeColors.axisLabel },
        axisLine: { lineStyle: { color: themeColors.gridLine } },
      },
      yAxis: config.yAxes.map(axis => ({
        type: 'value',
        name: axis.name,
        position: axis.position,
        nameLocation: 'middle',
        nameGap: axis.nameGap || 50,
        min: axis.min,
        max: axis.max,
        nameTextStyle: { fontSize: 12, color: themeColors.axisLabel, align: 'center' },
        nameRotate: 90,
        axisLabel: {
          formatter: axis.formatter === '{value}%'
            ? (value: number) => `${value}%`
            : axis.formatter === ''
              ? () => ''
              : (value: number) => formatK(value),
          fontSize: 11,
          color: themeColors.axisLabel,
        },
        axisLine: { lineStyle: { color: themeColors.gridLine } },
        splitLine: {
          show: true,
          lineStyle: {
            type: axis.position === 'right' ? 'dashed' : 'solid',
            color: themeColors.gridLine,
            opacity: axis.position === 'right' ? 0.5 : 0.3,
          },
        },
      })),
      series: seriesData,
      dataZoom: [
        { type: 'inside', start: 0, end: 100 },
        { type: 'slider', start: 0, end: 100, height: 20, bottom: 10 },
      ],
    };
  };

  return (
    <div
      className="rounded-xl border border-card-border bg-card text-card-foreground shadow-sm px-4 pb-4 pt-2"
      style={{ height: isFullscreen ? Math.min(window.innerHeight - 250, 700) : 'auto' }}
    >
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-base font-semibold text-foreground">{config.title}</h2>
        <div className="flex gap-1">
          <button
            onClick={onFullscreenToggle}
            className="h-8 w-8 flex items-center justify-center rounded-md bg-background text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <button
            onClick={handleMenuOpen}
            className="h-8 w-8 flex items-center justify-center rounded-md bg-background text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label="chart options"
            aria-controls={menuOpen ? 'chart-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? 'true' : undefined}
          >
            <EllipsisVertical className="h-4 w-4" />
          </button>
        </div>
      </div>

      <Menu
        id="chart-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        MenuListProps={{ 'aria-labelledby': 'chart-options-button' }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'var(--color-popover, var(--popover))',
              color: 'var(--color-foreground, var(--foreground))',
              borderColor: 'var(--color-border, var(--border))',
              border: '1px solid',
            },
          },
        }}
      >
        <MenuItem onClick={handleToggleView} sx={{ color: 'inherit' }}>
          {showTable ? <ChartLine className="h-4 w-4 mr-2" /> : <TableProperties className="h-4 w-4 mr-2" />}
          {showTable ? 'Show as Chart' : 'Show as Table'}
        </MenuItem>
        <MenuItem onClick={handleExportCSV} disabled={chartData.length === 0} sx={{ color: 'inherit' }}>
          <Download className="h-4 w-4 mr-2" />
          Export as CSV
        </MenuItem>
        <MenuItem onClick={handleSaveAsImage} disabled={chartData.length === 0 || showTable} sx={{ color: 'inherit' }}>
          <ImageDown className="h-4 w-4 mr-2" />
          Save as Image
        </MenuItem>
        <MenuItem onClick={handleExportPdf} disabled={chartData.length === 0} sx={{ color: 'inherit' }}>
          <FileDown className="h-4 w-4 mr-2" />
          Export as PDF
        </MenuItem>
      </Menu>

      {loading && (
        <div className="flex justify-center items-center" style={{ height: chartHeight }}>
          <CircularProgress sx={{ color: 'var(--color-primary, var(--primary))' }} />
        </div>
      )}

      {error && !loading && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive mb-2">
          {error}
        </div>
      )}

      {!loading && !error && chartData.length === 0 && (
        <div className="rounded-md border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
          No data available for the selected time range
        </div>
      )}

      {!loading && !error && chartData.length > 0 && !showTable && (
        <ReactECharts
          ref={chartRef}
          option={getOption()}
          style={{ height: `${chartHeight}px`, width: '100%' }}
          notMerge={true}
          lazyUpdate={true}
          theme="default"
        />
      )}

      {!loading && !error && chartData.length > 0 && showTable && (
        <div className="overflow-auto rounded-md border border-border" style={{ maxHeight: chartHeight }}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-foreground border-b border-border">Timestamp</th>
                {config.tableColumns.map((col, idx) => (
                  <th key={idx} className="px-3 py-2 text-left font-semibold text-foreground border-b border-border">
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chartData.map((row, index) => (
                <tr key={index} className="hover:bg-accent/50 transition-colors">
                  <td className="px-3 py-2 text-foreground border-b border-border">
                    {dayjs.utc(row.timestamp).format('MMM DD, YYYY HH:mm')}
                  </td>
                  {config.tableColumns.map((col, idx) => (
                    <td key={idx} className="px-3 py-2 text-foreground border-b border-border">
                      {col.isPercentage
                        ? `${row[col.dataKey].toFixed(2)}%`
                        : row[col.dataKey].toLocaleString()}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GlobalCharts;
